import { LOCATION_CHANGE, CALL_HISTORY_METHOD } from 'connected-react-router';
import { unmountComponentAtNode } from 'react-dom';
import { matchPath } from 'react-router';
import { RProgressApi } from '@hh.ru/rprogress';

import Debug from 'bloko/common/core/Debug';
import scrollToElement from 'bloko/common/scrollToElement';
import urlParser from 'bloko/common/urlParser';

import { hasInjectData, inject, extract, hasActionsCache } from './injector';
import scrollCorrection from './scrollCorrection';
import { isInternal } from './url';
import Link from './Link';

export const HHTM_SOURCE_HEADER = 'X-hhtmSource';
export const HHTM_FROM_HEADER = 'X-hhtmFrom';

export { Link };

const processRouteNotExist = (reactRoot, url, needDesyroy, isHistoryBack) => {
    if (reactRoot && needDesyroy && isHistoryBack) {
        unmountComponentAtNode(reactRoot);
        return;
    }

    window.location.assign(url);
};

const makeSPAMiddleware = (
    fetcher,
    routes,
    headers,
    rootReducer,
    makeSetStoreField,
    SET_FIELD_DATA_AS_IS,
    showDefaultError,
    resetSelectors,
    globalVars = window.globalVars,
    reactRoot
) => {
    const fetchingDataAction = makeSetStoreField('fetchingData');

    let cancelScrollCorrection = false;

    const isCanceledFetchingData = (action) => action?.payload?.args?.[2]?.cancelFetchingData;

    const getRoute = (url) => {
        for (const route of routes) {
            const { path, exact = true, strict } = route;
            const match = matchPath(url, { path, exact, strict: !!strict });
            if (match) {
                return { ...route, match };
            }
        }
        return undefined;
    };

    const injectData = ({ getState, result, action, next, urlWasChanged }) => {
        try {
            inject(
                fetchingDataAction,
                SET_FIELD_DATA_AS_IS,
                result,
                {
                    noTrl: getState().router?.location?.query?.notrl,
                },
                resetSelectors,
                rootReducer,
                globalVars
            );
            next(urlWasChanged ? extract(action) : action);
        } catch (error) {
            Debug.log('error out', error);
            throw error;
        }
    };

    const processError = (dispatch, error) => {
        if (!fetcher.isCancel(error)) {
            cancelScrollCorrection = true;
            dispatch(showDefaultError(error));
        }
    };

    const fetchPageData = ({ action, next, parsedUrl, hhtmFrom, getState, dispatch, preload, urlWasChanged }) => {
        RProgressApi.start();
        dispatch(fetchingDataAction(true));
        return Promise.all([
            fetcher.getPage(parsedUrl.pathname, {
                params: parsedUrl.params,
                headers: {
                    ...headers,
                    [HHTM_SOURCE_HEADER]: '',
                    [HHTM_FROM_HEADER]: hhtmFrom || '',
                },
            }),
            preload,
        ]).then(
            ([result]) => {
                RProgressApi.complete();
                injectData({ getState, result, action, next, urlWasChanged });

                return result;
            },
            (error) => {
                RProgressApi.complete();
                if (error?.response?.status === 406 || error?.response?.status === 403) {
                    // Для редиректа нужно поставить hhtmFrom обратно в ссылку
                    parsedUrl.params.hhtmFrom = hhtmFrom || null;
                    window.location.assign(parsedUrl.href);
                    return undefined;
                }

                if (error?.response?.status < 500 && hasInjectData(rootReducer, error.response.data)) {
                    injectData({ getState, result: error.response.data, action, next, urlWasChanged });
                    return error.response.data;
                }

                return processError(dispatch, error);
            }
        );
    };

    let cancelFetchingData = false;

    const getParsedUrlAndHhtm = (url) => {
        const parsedUrl = urlParser(url);

        // Если в ссылке есть hhtmFrom, используем его, иначе hhtmSource страницы.
        const currentHhtmSource = globalVars?.analyticsParams?.hhtmSource;
        const hhtmFrom = parsedUrl.params.hhtmFrom?.[0] || currentHhtmSource;

        // hhtmSource определяется сервером и не может быть произвольным.
        // hhtmFrom не нужен в урле, т.к. отправится в заголовках.
        delete parsedUrl.params.hhtmSource;
        delete parsedUrl.params.hhtmFrom;

        return [parsedUrl, hhtmFrom];
    };

    const needForceReload = (getState, route) => {
        const currentRoute = getRoute(getState().router.location?.pathname);
        return !route || route.forceReload || !currentRoute || currentRoute?.forceReload;
    };

    return ({ dispatch, getState }) =>
        (next) =>
        (action) => {
            if (action.type === LOCATION_CHANGE) {
                cancelScrollCorrection = !!action.payload?.state?.cancelScrollCorrection;
            }

            if (action.type === CALL_HISTORY_METHOD) {
                if (isCanceledFetchingData(action)) {
                    cancelFetchingData = true;
                    return next(action);
                }

                // Тут может быть объект Location
                let url = action.payload.args[0] && String(action.payload.args[0]);
                if (url && url.startsWith && url.startsWith('?')) {
                    url = getState().router.location.pathname + url;
                }
                const [parsedUrl, hhtmFrom] = getParsedUrlAndHhtm(url);

                // Нужно смотреть именно на origin, потому что request.host
                // неправильно работает на hh.ru.STAND.pyn.ru (значение остаётся hh.ru).
                // А в request.origin приходит https://hh.ru.STAND.pyn.ru
                const origin = getState().request?.origin || null;
                const route = getRoute(parsedUrl.pathname);
                const isExternalURL = !isInternal(url, origin);

                if (isExternalURL || needForceReload(getState, route)) {
                    parsedUrl.params.hhtmFrom = hhtmFrom || null;
                    processRouteNotExist(
                        reactRoot,
                        isExternalURL ? url : parsedUrl.href,
                        getState().hasDestroyIfAppRouteNotExist
                    );

                    return undefined;
                }

                fetchPageData({
                    action,
                    next,
                    parsedUrl,
                    hhtmFrom,
                    dispatch,
                    getState,
                    preload: route.component.preload(),
                }).finally(() => {
                    if (!cancelScrollCorrection) {
                        // важно получить location после выполнения fetchPageData, иначе он будет старый
                        scrollCorrection(
                            getState().router.location && getState().router.location.hash,
                            false,
                            scrollToElement
                        );
                    }
                });

                return undefined;
            }

            /*
           При навигации "вперед", "назад" у пользователя вызывается только LOCATION_CHANGE event
           Так происходит потому что react-router-redux работает следующим образом:
           1) CALL_HISTORY_METHOD описывает какой метод у объекта history нужно вызывать.
           2) Этот экшен летит в middleware
           3) В middleware этот метод вызывается (например history.push(someUrl))

           В случае с "вперед", "назад" history вызывается "не нами", поэтому есть только один экшен LOCATION_CHANGE
           Для простоты кода мы не вводим дополнительных условий на модель инжекта данных.

           Поэтому в случае с forward/backward у нас в cтор летит: LOCATION_CHANGE, [батч данных]
           а в случае с обычной ссылкой: CALL_HISTORY_METHOD, [батч данных + LOCATION_CHANGE]
        */

            if (
                action.type === LOCATION_CHANGE &&
                !action.payload.isFirstRendering &&
                !hasActionsCache() &&
                !cancelFetchingData
            ) {
                const { pathname, search, hash } = action.payload.location;
                const { pathname: statePathname, search: stateSearch } = getState().router.location;

                if (pathname === statePathname && search === stateSearch) {
                    return undefined;
                }

                const route = getRoute(pathname);
                const [parsedUrl, hhtmFrom] = getParsedUrlAndHhtm(`${pathname}${search}${hash}`);

                if (needForceReload(getState, route)) {
                    processRouteNotExist(reactRoot, parsedUrl.href, getState().hasDestroyIfAppRouteNotExist, true);
                    return undefined;
                }

                fetchPageData({
                    action,
                    next,
                    parsedUrl,
                    hhtmFrom,
                    dispatch,
                    getState,
                    preload: route.component.preload(),
                    urlWasChanged: true,
                }).finally(() => {
                    if (!cancelScrollCorrection) {
                        scrollCorrection(getState().router.location.hash, true, scrollToElement);
                    }
                });

                return undefined;
            }

            if (action.type === LOCATION_CHANGE && !action.payload.isFirstRendering && cancelFetchingData) {
                cancelFetchingData = false;
            }

            return next(extract(action));
        };
};

export default makeSPAMiddleware;
